import React from 'react';
import { Box, Typography, Button, IconButton, Card } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { IPaymentCard } from '../../types/global/generic.types';
import MasterCard from '../CustomSVGIcons/MasterCard';
import VisaCard from '../CustomSVGIcons/VisaCard';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import CircularCheckmark from '../CustomSVGIcons/CircularCheckIcon';

export interface PaymentCardProps {
    isDefault?: boolean;
    isExpired?: boolean;
    card?: IPaymentCard;
    onSetDefault?: () => void;
    onDelete?: () => void;
    isModalMode?: boolean;
    isSelected?: boolean;
    cardSize?: string;
}

const PaymentCard: React.FC<PaymentCardProps> = ({
    card,
    isDefault = false,
    isExpired = false,
    onSetDefault,
    onDelete,
    isModalMode,
    isSelected,
    cardSize,
}) => {
    let CardRender = MasterCard;
    const { themeStyle } = useThemeContext();
    switch (card?.authorization.brand) {
        case 'visa':
            CardRender = VisaCard;
            break;
    }

    return (
        <Card
            variant="outlined"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 2,
                marginBottom: 1,
                borderColor: isDefault ? theme[themeStyle].highlight : theme[themeStyle].darkGray60,
                width: '100%',
                borderRadius: 2,
                backgroundColor: theme[themeStyle].deep_grey_lighter,
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}>
            <Box display="flex" alignItems="center" sx={{ overflow: 'hidden', flexGrow: 1 }}>
                <Box sx={{ flexShrink: 0 }}>
                    <CardRender
                        width={cardSize ?? '100px'}
                        height={cardSize ?? '100px'}
                        stroke={theme[themeStyle].tag_white}
                    />
                </Box>
                <Box pl={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>{`${card?.authorization.brand} ending in ${card?.authorization.last4}`}</Typography>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        color={isExpired ? 'error.main' : 'text.secondary'}>
                        Exp. date {card?.authorization?.exp_month}/{card?.authorization?.exp_year}
                    </Typography>
                </Box>
            </Box>
            {isModalMode && isSelected && (
                <Box sx={{ flexShrink: 0, ml: 1 }}>
                    <CircularCheckmark filled color={theme[themeStyle].highlight} size={20} />
                </Box>
            )}
            {!isModalMode && (
                <Box display="flex" alignItems="center" sx={{ flexShrink: 0 }}>
                    {isDefault ? (
                        <Button
                            style={{
                                color: theme[themeStyle].darkGray60,
                                backgroundColor: theme[themeStyle].deep_grey,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 12,
                            }}
                            disabled>
                            Default
                        </Button>
                    ) : isExpired ? (
                        <Typography variant="body2" color="error.main">
                            Expired
                        </Typography>
                    ) : (
                        <Button variant="text" color="primary" size="small" onClick={onSetDefault}>
                            Set as Default
                        </Button>
                    )}
                    <IconButton color="error" onClick={onDelete}>
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )}
        </Card>
    );
};

export default PaymentCard;
